import { useState } from 'react';
import { InlineStack, Select } from '@customerio/pluma-components/react';
import { fieldError, type BuilderError } from '@customerio/dryad';
import { isEmptyString } from '@/utils/string';
import { clearsValues, isTwoValueFilterOperator } from '../../attribute-filter-state';
import type { OperatorGroup } from '../../operator-groups';
import { timestampOperatorForVariant } from '../../operators';
import {
  isAbsoluteTimestampOperator,
  isTimestampInequalityOperator,
  normalizeTimestampOperatorForSelect,
} from '../../relative-timestamp';
import { TimestampVariantSelect } from './timestamp-editors';
import { ValueEditor } from './value-editor';
import { valueForOperatorChange } from './value-for-operator-change';
import type { OperatorValuePatch, ValueOption } from './types';

export type OperatorValueProps = {
  /** The attribute the condition names — the date tooltip reads it. */
  field: string;
  operator: string;
  value: string | null;
  value2: string | null;
  /**
   * The operator dropdown, derived by the row from the families its surface
   * declared, so the menu and the plugin's parse claim can't disagree.
   */
  operatorGroups: OperatorGroup[];
  valueOptions: ValueOption[];
  isLoadingValueOptions?: boolean;
  disabled?: boolean;
  /** The node's errors; the editor places them on the input each one names. */
  errors?: BuilderError[];
  /** What the value field says while empty; "Value" by default. */
  valuePlaceholder?: string;
  onChange: (patch: OperatorValuePatch) => void;
};

const NO_ERRORS: BuilderError[] = [];

/**
 * The operator select plus whichever value editor the chosen operator needs.
 *
 * Which editors are reachable follows from the operator families a surface
 * declares — one offering only equality and existence never renders a date
 * picker, without having to say so here.
 */
export function OperatorValue({
  field,
  operator,
  value,
  value2,
  operatorGroups,
  valueOptions,
  isLoadingValueOptions,
  disabled,
  errors = NO_ERRORS,
  valuePlaceholder,
  onChange,
}: OperatorValueProps) {
  // "Enter a value" renders only once the input has been visited — or from
  // the start when the row mounted holding a value, whose problems block the
  // commit either way.
  const [valueTouched, setValueTouched] = useState(() => !isEmptyString(value));

  // Always shown, untouched or not: an operator-tagged mismatch makes the
  // tree invalid — which stops the surface committing, so the row has to say
  // why rather than leave the editor looking like it simply stopped working.
  const operatorError = fieldError(errors, 'operator');
  const valueError = valueTouched ? fieldError(errors, 'value') : undefined;

  const handleOperatorChange = (nextOperator: string | null) => {
    if (nextOperator == null) {
      return;
    }
    // The dropdown lists only the relative spelling of a paired timestamp
    // operator, so a pick keeps whichever variant the row is already on.
    const variant = isAbsoluteTimestampOperator(operator) ? 'absolute' : 'relative';
    const transformed = timestampOperatorForVariant(nextOperator, variant);
    if (transformed === operator) {
      return;
    }
    const nextValue = valueForOperatorChange(transformed, operator, value);
    // Switching operators must not inherit touched state; a carried non-blank
    // value keeps showing its problems while a cleared one starts clean.
    setValueTouched(!isEmptyString(nextValue));
    onChange({
      operator: transformed,
      value: nextValue,
      value2: clearsValues(transformed, operator) || !isTwoValueFilterOperator(transformed) ? null : value2,
    });
  };

  return (
    <InlineStack alignBlock="baseline" gap="050" wrap>
      <Select
        data-testid="dryad-filter-operator-select"
        ariaLabel="Operator"
        size="sm"
        options={operatorGroups}
        value={normalizeTimestampOperatorForSelect(operator)}
        error={operatorError}
        onChange={handleOperatorChange}
        isDisabled={disabled}
      />
      {isTimestampInequalityOperator(operator) ? (
        <TimestampVariantSelect disabled={disabled} operator={operator} value={value} onChange={onChange} />
      ) : null}
      <ValueEditor
        field={field}
        operator={operator}
        value={value}
        value2={value2}
        valueOptions={valueOptions}
        isLoadingValueOptions={isLoadingValueOptions}
        disabled={disabled}
        valueError={valueError}
        valuePlaceholder={valuePlaceholder}
        onChange={onChange}
        onValueBlur={() => setValueTouched(true)}
      />
    </InlineStack>
  );
}
